import { digger } from '@cbre/digger';
import {
  areaUomAbbrev,
  concatValuesBy,
  defaultTxt,
  EMPTY_STRING,
  formatNumber,
  simpleValueValidation,
} from '@miq/utils';

import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { Dash, displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { getUOMValue, RoleDescriptions } from '../us.common';
import { formatCompanyContactByRole, formatContactByRole } from './us-tim.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  173: {
    // 173 Tenant Name/Industry
    displayFn: (record: ReportRecord, _, arg1, arg2) => {
      const tenantName = mapAndFormatDynamicValue(record, arg1);
      const tenantIndustry = mapAndFormatDynamicValue(record, arg2);

      const values = concatValuesBy(' \n\n', findTenantValues(tenantName), findTenantValues(tenantIndustry));
      return values;
    },
  },
  174: { displayFn: displayFnWrapper }, // 174 - Current Size
  333: {
    // 333 - Current Location
    displayFn: (record, _, arg1, arg2, arg3, arg4) => {
      return formatMultipleData(record, '|', [arg1, arg2, arg3, arg4]);
    },
  },
  175: {
    // 175 - Preferred Location
    displayFn: (record, _, arg1, arg2, arg3, arg4) => {
      return formatMultipleData(record, '|', [arg1, arg2, arg3, arg4]);
    },
  },
  176: {
    // 176 - Timing/Type
    displayFn: (record: ReportRecord, _, arg1, arg2) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, arg1, null, null, null, ''),
        mapAndFormatDynamicValue(record, arg2, null, null, null, '')
      );
      return values;
    },
  },
  177: {
    // 177 - Representation
    displayFn: (record: ReportRecord, _, arg1, arg2) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, arg1),
        mapAndFormatDynamicValue(record, arg2)
      );
      return values;
    },
  },
  178: {
    // 178 - Size
    displayFn: (record: ReportRecord, _, arg1, arg2) => {
      let minSize = mapAndFormatDynamicValue(record, arg1);
      let maxSize = mapAndFormatDynamicValue(record, arg2);
      if (minSize[0] === Dash) {
        minSize = '- SF';
      }
      if (maxSize[0] === Dash) {
        maxSize = '- SF';
      }
      const result = concatValuesBy(' -\n', minSize, maxSize);
      return result;
    },
  },
  179: {
    // 179 - Entered/Last Update
    displayFn: (record: ReportRecord, _, arg1, arg2) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, arg2, null, null, null, ''),
        mapAndFormatDynamicValue(record, arg1, null, null, null, '')
      );
      return values;
    },
  },
  180: {
    displayFn: (record: ReportRecord, _, __) => {
      return formatContactByRole(record, RoleDescriptions.ReportingBroker);
    },
  }, // 180 - Submitting Broker
  181: { displayFn: displayFnWrapper }, // 181 - Comments
  221: {
    // 221 - Total Size
    displayFn: (record: ReportRecord[], _, arg1) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const maxSize = digger(rec.data, arg1.field);
        const uomValue = digger(rec.data, arg1.uomArgument.field);
        uom = getUOMValue(uom, uomValue);
        if (maxSize) {
          totalSize += parseInt(maxSize);
        }
      });
      return arg1.uomArgument.lovValues.pipe(
        map((currencyLOV: LovValues[]) => {
          if (uom && currencyLOV) {
            const lovValue = currencyLOV.find(a => a.key === uom);
            if (lovValue) {
              uom = areaUomAbbrev(lovValue.value);
            }
          }
          return formatNumber(totalSize) + ' ' + (uom || '');
        })
      );
    },
  },
  251: {
    // 251 - Tenant Agency Rep
    displayFn: (record: ReportRecord, _, arg) => {
      const result = formatCompanyContactByRole(record, arg, RoleDescriptions.TenantRep);
      return result === defaultTxt || result === EMPTY_STRING ? ' ' : result;
    },
  },
};

function formatMultipleData(record: any, separator: any, args: any[]): string {
  let result = '';
  args.forEach(x => {
    const values = mapAndFormatDynamicValue(record, x);
    if (typeof values === 'string') {
      if (values !== defaultTxt) {
        result = concatValuesBy(separator, result, values);
      }
    } else {
      const nonEmptyValues = values ? checkForEmpty(values) : [];
      if (nonEmptyValues.length > 0) {
        const nonEmptyValue = concatValuesBy(', ', ...nonEmptyValues);
        result = concatValuesBy(separator, result, nonEmptyValue);
      }
    }
  });
  return result === '' ? defaultTxt : result;
}

function checkForEmpty(values: string[]): string[] {
  return values.filter(val => val !== defaultTxt);
}

function findTenantValues(value: string | string[]) {
  let val;
  if (Array.isArray(value)) {
    val = value.map(element => {
      if (element) {
        const withNoDigitsElement = element.replace(/[0-9]/g, '').replace(/_/g, '').trim();
        return simpleValueValidation(withNoDigitsElement) ? withNoDigitsElement : '';
      } else {
        return simpleValueValidation(element) ? element : '';
      }
    });
  } else if (value) {
    const withNoDigitsElement = value.replace(/[0-9]/g, '').replace(/-/g, '').trim();

    val = simpleValueValidation(withNoDigitsElement) ? withNoDigitsElement : '';
  }
  return val;
}
